import { useZero } from "@rocicorp/zero/react";
import { STORAGE_USAGE_REFRESH_INTERVAL_MS } from "../../../shared/monitoringWorkflowsReport";
import { sharedMutators } from "../../../shared/mutators";
import { ConfirmModal } from "../ConfirmModal";
import { useToast } from "../Toast";
import type { StorageVolumeTarget } from "./storageUsageLevel";

export type StorageDeletion = {
  target: StorageVolumeTarget;
  // A file or directory in the volume; the whole volume when absent.
  path?: string;
};

function description({ target, path }: StorageDeletion): string {
  if (path !== undefined) {
    return `“${path}” is deleted from ${target.volumeName}. This can’t be undone.`;
  }
  switch (target.category) {
    case "live_volume":
      return "Every file in this live volume is deleted, and the space’s workflows start with it empty on their next run. This can’t be undone.";
    case "branch_volume":
      return "Every file this draft stored in the volume is deleted. Live’s data is not affected.";
    case "personal_volume":
      return "Every file in this chat volume is deleted. This can’t be undone.";
    case "run_volume":
      return "The volume data kept from this workflow’s past runs is deleted. Future runs are not affected.";
    case "step_test_volume":
      return "The volume copies kept from this workflow’s step tests are deleted. This can’t be undone.";
  }
}

function mutation({ target, path }: StorageDeletion) {
  const { volumeName } = target;
  switch (target.category) {
    case "run_volume":
    case "step_test_volume":
      return sharedMutators.deleteWorkflowRunVolumeData({
        workflowId: target.workflowId,
        volumeId: target.volumeId,
        category: target.category,
        branchId: target.branchId ?? undefined,
      });
    case "personal_volume":
      return sharedMutators.deletePersonalVolumeData({ volumeName, path });
    case "live_volume":
      return sharedMutators.deleteLiveVolumeData({
        spaceId: target.spaceId,
        volumeName,
        path,
      });
    case "branch_volume":
      // Draft volume refs are always labeled with their branch.
      return sharedMutators.deleteBranchVolumeData({
        branchId: target.branchId!,
        volumeName,
        path,
      });
  }
}

export function StorageDeleteConfirm({
  deletion,
  onClose,
  onDeleted,
}: {
  deletion: StorageDeletion;
  onClose: () => void;
  onDeleted?: () => void;
}) {
  const z = useZero();
  const { showToast } = useToast();
  const { target, path } = deletion;

  const confirm = () => {
    onClose();
    void z.mutate(mutation(deletion)).server.then((result) => {
      if (result.type === "error") {
        showToast({
          icon: "circle-off",
          message: result.error.message,
          buttonLabel: "Dismiss",
        });
        return;
      }
      showToast({
        icon: "check",
        message: `Deleted. Storage usage updates within ${STORAGE_USAGE_REFRESH_INTERVAL_MS / 60_000} minutes.`,
        buttonLabel: "Dismiss",
      });
      onDeleted?.();
    });
  };

  return (
    <ConfirmModal
      open
      onClose={onClose}
      onConfirm={confirm}
      title={
        path === undefined
          ? `Delete ${target.volumeName}’s data?`
          : `Delete ${path.split("/").at(-1)}?`
      }
      description={description(deletion)}
      confirmLabel="Delete"
      destructive
    />
  );
}
